/**
 * Strip HTML from scraped/API text for safe plain-text display in React.
 * Prefer this over dangerouslySetInnerHTML for opportunity descriptions and list items.
 */

function decodeHtmlEntities(text: string): string {
  if (typeof document !== 'undefined') {
    const el = document.createElement('textarea');
    el.innerHTML = text;
    return el.value;
  }

  return text
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&apos;/gi, "'")
    .replace(/&mdash;/gi, '—')
    .replace(/&ndash;/gi, '–')
    .replace(/&hellip;/gi, '…')
    .replace(/&#(\d+);/g, (_, code: string) => String.fromCharCode(Number(code)))
    .replace(/&#x([0-9a-f]+);/gi, (_, hex: string) => String.fromCharCode(parseInt(hex, 16)));
}

const SCRIPT_LIKE_TAG_PATTERN =
  /<\s*(?:script|style|noscript|iframe)\b[^>]*>[\s\S]*?<\s*\/\s*(?:script|style|noscript|iframe)\s*>/gi;

const HTML_COMMENT_PATTERN = /<!--[\s\S]*?-->/g;

const INLINE_JS_PATTERNS: RegExp[] = [
  /googletag\s*\.\s*cmd\s*\.\s*push\s*\([\s\S]*?\}\s*\)\s*;?/gi,
  /googletag\s*\.\s*display\s*\([\s\S]*?\)\s*;?/gi,
  /document\s*\.\s*write(?:ln)?\s*\([\s\S]*?\)\s*;?/gi,
  /\(function\s*\(\s*\)\s*\{[\s\S]*?\}\s*\)\s*\(\s*\)\s*;?/gi,
  /\{\s*googletag[\s\S]*?\}\s*\)\s*;?/gi,
];

function stripInlineJsPatterns(text: string): string {
  let cleaned = text;
  let previous = '';

  while (cleaned !== previous) {
    previous = cleaned;
    for (const pattern of INLINE_JS_PATTERNS) {
      cleaned = cleaned.replace(pattern, '');
    }
  }

  return cleaned;
}

function stripScriptsAndInlineJs(text: string): string {
  let cleaned = text.replace(SCRIPT_LIKE_TAG_PATTERN, '');
  cleaned = cleaned.replace(HTML_COMMENT_PATTERN, '');
  cleaned = stripInlineJsPatterns(cleaned);
  return cleaned;
}

/**
 * Convert HTML-ish scraped content to readable plain text.
 * Block tags become line breaks; remaining tags are removed; entities are decoded.
 */
/** Alias for RSS/news card excerpts and other short plain-text displays. */
export const stripHtml = stripHtmlToPlainText;

export function stripHtmlToPlainText(text: string | null | undefined): string {
  if (text == null || typeof text !== 'string') return '';

  let normalized = stripScriptsAndInlineJs(text.replace(/\r\n/g, '\n').replace(/\r/g, '\n'));

  normalized = normalized.replace(/<\s*br\s*\/?>/gi, '\n');
  normalized = normalized.replace(
    /<\s*\/\s*(p|div|li|h[1-6]|tr|table|section|article|ul|ol)\s*>/gi,
    '\n'
  );
  normalized = normalized.replace(
    /<\s*(p|div|li|h[1-6]|tr|table|section|article|ul|ol)[^>]*>/gi,
    ''
  );
  normalized = normalized.replace(/<[^>]+>/g, '');
  normalized = decodeHtmlEntities(normalized);

  return normalized
    .split('\n')
    .map((line) => line.replace(/\u00A0/g, ' ').replace(/[ \t]+/g, ' ').trim())
    .join('\n')
    .replace(/\n{2,}/g, '\n')
    .trim();
}
